import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, BookOpen } from 'lucide-react';
import { useAppData } from '../context/AppDataContext';
import { RECORD_TYPES, recordTimeMs } from '../utils/recordUtils';
import { getPageScale } from '../utils/pageScale';

const MAX_BOOKS = 5;
const MAX_RECORDS = 12;

/** 제목·저자·한줄평·기록 본문을 한 번에 찾는다 (⌘K / Ctrl+K) */
const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { books, records } = useAppData();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    inputRef.current?.focus();

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const bookById = useMemo(() => new Map(books.map(b => [b.id, b])), [books]);

  const { bookHits, recordHits } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return { bookHits: [], recordHits: [] };

    return {
      bookHits: books
        .filter(b =>
          b.title?.toLowerCase().includes(q) ||
          b.author?.toLowerCase().includes(q) ||
          b.oneLiner?.toLowerCase().includes(q)
        )
        .slice(0, MAX_BOOKS),
      recordHits: records
        .filter(r => r.text?.toLowerCase().includes(q))
        .sort((a, b) => recordTimeMs(b) - recordTimeMs(a))
        .slice(0, MAX_RECORDS)
    };
  }, [query, books, records]);

  if (!open) return null;

  const go = (bookId) => {
    navigate(`/book/${bookId}`);
    onClose();
  };

  const hasQuery = query.trim().length > 0;
  const noHits = hasQuery && bookHits.length === 0 && recordHits.length === 0;

  return createPortal(
    <div className="fixed inset-0 z-[65] flex items-start justify-center p-4 pt-[10vh]">
      <div className="absolute inset-0 bg-ink-950/50" onClick={onClose} />

      <div className="relative w-full max-w-xl max-h-[70vh] flex flex-col rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-950 shadow-2xl">
        <div className="flex items-center gap-3 px-4 h-14 border-b border-ink-200 dark:border-ink-800 shrink-0">
          <Search size={16} strokeWidth={1.5} className="text-ink-500 dark:text-ink-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="책 제목, 저자, 기록 내용 검색"
            className="flex-1 bg-transparent text-sm font-sans text-ink-900 dark:text-ink-100 placeholder-ink-400 focus:outline-none"
          />
          <button
            onClick={onClose}
            className="p-1 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors shrink-0"
            aria-label="닫기"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {!hasQuery && (
            <p className="py-16 text-center text-sm text-ink-600 dark:text-ink-300 font-sans">
              찾고 싶은 문장이나 책 이름을 입력하세요
            </p>
          )}

          {noHits && (
            <p className="py-16 text-center text-sm text-ink-600 dark:text-ink-300 font-sans">
              결과가 없어요
            </p>
          )}

          {bookHits.length > 0 && (
            <div className="p-2">
              <p className="label px-2 py-2">책</p>
              {bookHits.map(book => (
                <button
                  key={book.id}
                  onClick={() => go(book.id)}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
                >
                  {book.imageUrl ? (
                    <img src={book.imageUrl} alt="" className="w-6 h-9 object-cover rounded-[2px] shrink-0" />
                  ) : (
                    <BookOpen size={16} strokeWidth={1.5} className="text-ink-500 dark:text-ink-400 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-sans">{book.title}</span>
                    <span className="block truncate text-xs text-ink-500 dark:text-ink-400 font-sans">{book.author}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {recordHits.length > 0 && (
            <div className="p-2 border-t border-ink-100 dark:border-ink-900">
              <p className="label px-2 py-2">기록</p>
              {recordHits.map(record => {
                const book = bookById.get(record.bookId);
                const isQuote = record.type === RECORD_TYPES.QUOTE;
                return (
                  <button
                    key={record.id}
                    onClick={() => go(record.bookId)}
                    className="w-full px-2 py-2.5 rounded-md text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-ink-500 dark:text-ink-400 truncate font-sans">
                        {book?.title || '삭제된 책'}
                      </span>
                      {record.page && (
                        <span className="text-[11px] text-ink-500 dark:text-ink-400 tabular-nums font-sans shrink-0">
                          p.{getPageScale(book).toDisplay(record.page)}
                        </span>
                      )}
                    </span>
                    <span
                      className={[
                        'mt-1 block line-clamp-2 text-sm leading-relaxed',
                        isQuote ? 'font-display italic' : 'font-sans'
                      ].join(' ')}
                    >
                      {record.text}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalSearch;
